import { GaugeData, GaugeConfig, GaugePlacement, ReferenceDateValue } from "@/lib/types/gauge";
import { DisplayFormat } from "@/lib/types";
import { hasAllBandValues } from "@/lib/utils/range-levels";

/**
 * Pure request/response shaping for the series cache service.
 *
 * Deliberately free of browser-only imports (posthog-js, the Supabase browser
 * client) so it can be shared by the client fetcher and by server-side callers
 * such as the embed Open Graph renderer. Error reporting is injected by the
 * caller via `onResultError` rather than being wired in here.
 */

export type SeriesResult = {
  gaugeData: GaugeData;
  gaugeConfig: GaugeConfig;
  // Echoed back by the cache service so a batched response can be matched to
  // the request that asked for each row.
  requestId?: string;
};

export type SeriesCacheRequest = {
  displayId: string;
  variant?: string;
  date?: string;
  asOf?: string;
  requestId?: string;
  referenceDates?: Record<string, { name: string }>;
};

/**
 * The public shared link a series request is made from. Only consulted when
 * there is no signed-in viewer: it is what bounds which gauges the embed
 * service account will fetch.
 */
export type SeriesEmbedScope = {
  kind: "gauge" | "dashboard";
  slug: string;
};

export type CacheRequestMetadata = {
  request: string;
  cfRay: string;
};

export type FetchSeriesResult = {
  results: SeriesResult[];
  metadata: CacheRequestMetadata;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RawSeriesResult = any;

export type ResultErrorReporter = (result: RawSeriesResult) => void;

export const sortSeriesResults = (results: SeriesResult[]): SeriesResult[] => {
  const compareValues = (a: string | null, b: string | null) => {
    if (a === b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    return a.localeCompare(b);
  };

  return [...results].sort((a, b) => {
    const dateComparison = compareValues(b.gaugeData.date || null, a.gaugeData.date || null);
    if (dateComparison !== 0) return dateComparison;
    return compareValues(b.gaugeData.realtime_end || null, a.gaugeData.realtime_end || null);
  });
};

export const buildRequestId = (req: SeriesCacheRequest) =>
  req.requestId ||
  [req.displayId, req.variant || "default", req.date || "latest", req.asOf ? `asOf:${req.asOf}` : null]
    .filter(Boolean)
    .join(" - ");

export const normalizeRequests = (requests: SeriesCacheRequest[]) =>
  requests.map((req) => ({
    displayId: req.displayId,
    ...(req.variant ? { variant: req.variant } : {}),
    ...(req.date ? { date: req.date } : {}),
    ...(req.asOf ? { asOf: req.asOf } : {}),
    ...(req.referenceDates ? { referenceDates: req.referenceDates } : {}),
    requestId: buildRequestId(req),
  }));

export const buildCacheKey = (requests: SeriesCacheRequest[]) =>
  normalizeRequests(requests)
    .map((req) => {
      const refDatesKey = req.referenceDates
        ? Object.entries(req.referenceDates)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, val]) => `${key}:${val.name}`)
            .join(",")
        : "";
      return [req.displayId, req.variant ?? "default", req.date ?? "latest", req.asOf ?? "latest", refDatesKey].join(
        "|",
      );
    })
    .sort()
    .join("||");

const finiteOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// Each field as the API sent it: a null stays null, never a number.
const mapGaugePlacement = (gauge: RawSeriesResult): GaugePlacement | undefined =>
  gauge && typeof gauge === "object"
    ? {
        percentile: finiteOrNull(gauge.percentile),
        percentileLag: finiteOrNull(gauge.percentileLag),
        band: finiteOrNull(gauge.band),
        bandLag: finiteOrNull(gauge.bandLag),
      }
    : undefined;

export const mapResultToGaugeData = (result: RawSeriesResult, onResultError?: ResultErrorReporter): GaugeData => {
  if (result.category === "unexpected" || result.category === "bad_request") {
    onResultError?.(result);
  }

  const vintages = Array.isArray(result.availableVintages) ? result.availableVintages : [];

  return {
    display_id: result.displayId ?? "",
    date: result.date ?? result.latestObservationDate ?? "", // TODO: ask Scott to return date or if this is fine to leave it for now
    date_tm1: result.dateLag ?? "",
    value: result.value?.value ?? 0,
    value_tm1: finiteOrNull(result.value?.value_lag),
    avg10y_value: result.value?.["10y_avg"] ?? 0,
    ranges_level_series: Array.isArray(result.value?.["10y_percentiles"]) ? result.value["10y_percentiles"] : [],
    realtime_start: result.vintageDate ?? "",
    realtime_end: result.vintageDate ?? "",
    earliest_vintage_date: vintages[0] ?? undefined,
    latest_vintage_date: vintages[vintages.length - 1] ?? undefined,
    error_category: result.category ?? undefined,
    error_details: result.details ?? undefined,
    multiplier: undefined,
    referenceDateValues:
      result.referenceDates && typeof result.referenceDates === "object"
        ? Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Object.entries(result.referenceDates).map(([key, val]: [string, any]) => [
              key,
              {
                name: val.name,
                value: val.value,
                observationDate: val.observationDate,
                vintageDate: val.vintageDate,
                pattern: val.pattern,
                // Kept absent when the worker didn't send it, so the app knows to place it itself.
                ...(val.percentile !== undefined ? { percentile: finiteOrNull(val.percentile) } : {}),
              } satisfies ReferenceDateValue,
            ]),
          )
        : undefined,
    gauge: mapGaugePlacement(result.gauge),
  };
};

export const mapResultToGaugeConfig = (result: RawSeriesResult): GaugeConfig => ({
  display_id: result.displayId ?? "",
  series_id: result.seriesId ?? "",
  display_name: result.displayName ?? "",
  display_format: (result.displayFormat ||
    (typeof result.displayUnits === "string" && result.displayUnits.toLowerCase().includes("percent")
      ? "percent"
      : "number")) as DisplayFormat,
  display_units: result.displayUnits ?? "",
  display_precision: result.displayPrecision ?? null,
  display_abbreviate: result.displayAbbreviate ?? null,
  display_variants: Array.isArray(result.availableVariants) ? result.availableVariants : undefined,
  default_display_variant: result.defaultVariant ?? undefined,
  frequency: result.frequency ?? "",
  next_release: result.nextReleaseDate ?? "",
  last_updated: result.vintageDate ?? "",
  description: result.description ?? "",
  tier: result.tier ?? 0,
  publisher: result.publisherName ?? "",
  publisher_url: result.publisherUrl ?? "",
  fred_url: result.fredUrl ?? "",
  fred_series_name: result.providerTitle ?? "",
  fred_series_id: result.providerSeriesId ?? "",
  earliest_observation_date: result.earliestObservationDate ?? "",
  latest_observation_date: result.latestObservationDate ?? "",
  variant: result.variant ?? "",
  provider: result.provider ?? undefined,
});

export type ResultError = {
  category: "bad_request" | "unexpected";
  details: string;
};

/** A row the cache couldn't serve: rendered as the error (thunderstorm) state, never cached. */
export const isFailedResult = (result: SeriesResult) => isFailedCategory(result.gaugeData.error_category);

const isFailedCategory = (category: string | undefined) => category === "unexpected" || category === "bad_request";

// Every row the cache serves carries its series' data tier, refusals included,
// so a row without one is a cache bug: it fails and is reported, rather than
// being given a tier that could unlock it.
const MISSING_TIER: ResultError = { category: "unexpected", details: "The cache returned no tier for this series" };

/**
 * A served row needs its value, 10-year average and nine band values to be
 * drawn. A row missing any of them fails and is reported, rather than being
 * drawn from a placeholder 0 that reads as a real value. A missing prior value
 * is fine: the gauge just has no lag marker.
 */
const missingGaugeValues = (result: RawSeriesResult): ResultError | null => {
  const missing = [
    finiteOrNull(result.value?.value) === null && "value",
    finiteOrNull(result.value?.["10y_avg"]) === null && "10-year average",
    !hasAllBandValues(result.value?.["10y_percentiles"]) && "band values",
  ].filter(Boolean);
  return missing.length > 0
    ? { category: "unexpected", details: `The cache returned no ${missing.join(", ")} for this series` }
    : null;
};

// Pass `error` to mark the placeholder as failed, so gauges render the
// thunderstorm state from error_category rather than from the empty date.
export const buildErrorResult = (displayId: string, error?: ResultError): SeriesResult => ({
  gaugeData: {
    display_id: displayId,
    date: "",
    date_tm1: "",
    value: 0,
    value_tm1: null,
    avg10y_value: 0,
    ranges_level_series: [],
    realtime_start: "",
    realtime_end: "",
    ...(error ? { error_category: error.category, error_details: error.details } : {}),
  },
  gaugeConfig: {
    display_id: displayId,
    series_id: "",
    display_name: "",
    display_format: "number" as DisplayFormat,
    display_units: "",
    frequency: "",
    last_updated: "",
    next_release: "",
    description: "",
    tier: 0,
    publisher: "",
    publisher_url: "",
    fred_url: "",
    fred_series_name: "",
    fred_series_id: "",
    earliest_observation_date: "",
    latest_observation_date: "",
    variant: "",
    provider: undefined,
  },
});

export const parseCacheResponse = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cacheJson: any,
  onResultError?: ResultErrorReporter,
): SeriesResult[] => {
  let results: SeriesResult[] | undefined;

  if (cacheJson && Array.isArray(cacheJson.results)) {
    results = cacheJson.results.map((result: RawSeriesResult): SeriesResult => {
      const requestId = result.requestId ? { requestId: result.requestId } : {};
      if (typeof result.tier !== "number" && !isFailedCategory(result.category)) {
        onResultError?.({ ...result, ...MISSING_TIER });
        return { ...buildErrorResult(result.displayId ?? "", MISSING_TIER), ...requestId };
      }
      // Rows with a category (refusals and failures) carry no data to check.
      const missingValues = result.category ? null : missingGaugeValues(result);
      if (missingValues) {
        onResultError?.({ ...result, ...missingValues });
        return { ...buildErrorResult(result.displayId ?? "", missingValues), ...requestId };
      }
      return {
        gaugeData: mapResultToGaugeData(result, onResultError),
        gaugeConfig: mapResultToGaugeConfig(result),
        ...requestId,
      };
    });
  }

  if (cacheJson && Array.isArray(cacheJson.errors)) {
    cacheJson.errors.forEach((error: RawSeriesResult) => {
      if (error.displayId) {
        if (!results) results = [];
        results.push({
          ...buildErrorResult(error.displayId, {
            category: error.category === "bad_request" ? "bad_request" : "unexpected",
            details: error.details ?? error.message ?? "The cache service returned an error for this gauge",
          }),
          ...(error.requestId ? { requestId: error.requestId } : {}),
        });
      }
    });
  }

  if (!results || results.length === 0) return [];

  return sortSeriesResults(results);
};

export const CACHE_ENDPOINT =
  process.env.NEXT_PUBLIC_CACHE_ENDPOINT_URL ?? "https://cache.dev.volascope.com/api/v2/data";

/**
 * The cache service's single-series route, a sibling of the batch `data`
 * route: `/api/[v1|v2/]ts/:displayId/:variant`. It returns the latest vintage
 * as parallel `dates`/`values` arrays.
 */
const TIME_SERIES_ENDPOINT = `${CACHE_ENDPOINT.replace(/\/+$/, "").replace(/\/data$/, "")}/ts`;

/** One series' full history in one variant, on the time-series route. */
export const timeSeriesUrl = ({ displayId, variant }: { displayId: string; variant: string }) =>
  `${TIME_SERIES_ENDPOINT}/${encodeURIComponent(displayId)}/${encodeURIComponent(variant)}`;

export const supabaseProjectIdFromUrl = (supabaseUrl: string): string => {
  try {
    return new URL(supabaseUrl).hostname.split(".")[0];
  } catch {
    return supabaseUrl.split("//")[1]?.split(".")[0] || "";
  }
};

/** Headers for a JSON POST to the cache service on `token`, from the browser or the server. */
export const cacheServiceHeaders = (token: string): Record<string, string> => {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
    "X-Supabase-Project-ID": supabaseProjectIdFromUrl(process.env.NEXT_PUBLIC_SUPABASE_URL ?? ""),
    Authorization: `Bearer ${token}`,
  };
  // Dev-only secret for the local cache service. NEXT_PUBLIC_ because the
  // browser sends it too; it's undefined outside development.
  if (process.env.NODE_ENV === "development") {
    const devSecret = process.env.NEXT_PUBLIC_CACHE_LOCAL_DEV_SHARED_SECRET_KEY;
    if (devSecret) headers["X-Volya-Dev-Secret"] = devSecret;
  }
  return headers;
};
